import React, { useState, useEffect } from "react";
import { Card, Form, Input, InputNumber, Select, Switch, Button, Space, message, Tabs, Tag, Divider } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFetchProductQuery,
  useFetchCategoriesQuery,
  useFetchAllergensQuery,
  useUpdateProductMutation,
  useCreateProductMutation,
} from "./label.service";

const { TextArea } = Input;
const { Option } = Select;

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const { data: productData, isLoading: productLoading } = useFetchProductQuery(id, { skip: !isEdit });
  const { data: categoriesData } = useFetchCategoriesQuery();
  const { data: allergensData } = useFetchAllergensQuery();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();

  const product = productData?.data?.product;
  const categories = categoriesData?.data?.categories || [];
  const allergens = allergensData?.data?.allergens || [];

  useEffect(() => {
    if (isEdit && product) {
      form.setFieldsValue({
        name: product.name,
        sku: product.sku,
        barcode: product.barcode,
        categoryId: product.categoryId?._id,
        description: product.description,
        storageLocation: product.storageLocation,
        prepGuide: product.prepGuide,
        isActive: product.isActive,
        customExpiryRule: product.customExpiryRule?.enabled || false,
        expiryDays: product.customExpiryRule?.days || 4,
        allergens: product.allergens?.map(a => a.allergenId?._id) || [],
      });
    } else {
      form.setFieldsValue({
        isActive: true,
        customExpiryRule: false,
        expiryDays: 4,
      });
    }
  }, [product, isEdit, form]);

  const handleSubmit = async (values) => {
    try {
      const productData = {
        ...values,
        customExpiryRule: {
          enabled: values.customExpiryRule,
          days: values.expiryDays,
          unit: "Days"
        },
        allergens: values.allergens?.map(allergenId => ({ allergenId })) || [],
      };

      if (isEdit) {
        await updateProduct({ id, data: productData }).unwrap();
        message.success("Product updated successfully");
      } else {
        await createProduct(productData).unwrap();
        message.success("Product created successfully");
      }
      navigate("/label/category");
    } catch (error) {
      message.error(error?.data?.message || "Operation failed");
    }
  };

  if (productLoading) return <Card loading />;

  return (
    <Card
      title={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/label/category")} />
          <span style={{ fontSize: "18px", fontWeight: "bold" }}>
            {isEdit ? "Edit Product" : "Add Product"}
          </span>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Tabs
          items={[
            {
              key: "basic",
              label: "Basic Info",
              children: (
                <>
                  <Form.Item
                    name="name"
                    label="Product Name"
                    rules={[{ required: true, message: "Please enter product name" }]}
                  >
                    <Input placeholder="e.g., SEA BREAM, COD, HAKE" />
                  </Form.Item>

                  <Form.Item name="sku" label="SKU">
                    <Input placeholder="Enter SKU" />
                  </Form.Item>
{/* 
                  <Form.Item name="barcode" label="Barcode (EAN-13 number) *Optional*">
                    <Input placeholder="Enter barcode" />
                  </Form.Item> */}

                  <Form.Item
                    name="categoryId"
                    label="Category"
                    rules={[{ required: true, message: "Please select category" }]}
                  >
                    <Select placeholder="Select category">
                      {categories.map(cat => (
                        <Option key={cat._id} value={cat._id}>{cat.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item name="description" label="Description">
                    <TextArea rows={3} />
                  </Form.Item>

                  <Form.Item name="isActive" label="State" valuePropName="checked">
                    <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                  </Form.Item>
                </>
              ),
            },
            {
              key: "expiry",
              label: "Expiry Settings",
              children: (
                <>
                  <Form.Item name="customExpiryRule" label="Custom Expiry Rule" valuePropName="checked">
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, curr) => prev.customExpiryRule !== curr.customExpiryRule}
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("customExpiryRule") ? (
                        <>
                          <Form.Item
                            name="expiryDays"
                            label="Number of days"
                            rules={[{ required: true, message: "Please enter expiry days" }]}
                          >
                            <InputNumber min={1} max={365} style={{ width: "100%" }} />
                          </Form.Item>
                          <div className="ant-form-item-explain">
                            Warning: does not include day of production
                          </div>
                        </>
                      ) : null
                    }
                  </Form.Item>

          <Form.Item name="storageLocation" label="Storage Location">
            <Select
              placeholder="Select storage location"
              options={[
                { label: "Fridge", value: "Fridge" },
                { label: "Freezer", value: "Freezer" },
                { label: "Ambient", value: "Ambient" },
              ]}
            />
          </Form.Item>
                </>
              ),
            },
            {
              key: "allergens",
              label: "Allergens",
              children: (
                <>
                  <Form.Item name="allergens" label="Allergens">
                    <Select mode="multiple" placeholder="Select allergens">
                      {allergens.map(allergen => (
                        <Option key={allergen._id} value={allergen._id}>
                          {allergen.name} ({allergen.type})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Divider />

                  <Form.Item name="prepGuide" label="Prep Guide">
                    <TextArea rows={4} placeholder="Create Prep Guide" />
                  </Form.Item>
                </>
              ),
            },
            // {
            //   key: "preview",
            //   label: "Label Preview",
            //   children: (
            //     <div style={{ textAlign: "center", padding: "20px" }}>
            //       <Card
            //         style={{
            //           width: "200px",
            //           margin: "0 auto",
            //           border: "1px solid #d9d9d9",
            //           borderRadius: "8px",
            //         }}
            //       >
            //         <Form.Item noStyle shouldUpdate>
            //           {() => (
            //             <>
            //               <h3 style={{ margin: 0 }}>{form.getFieldValue("name") || "PRODUCT NAME"}</h3>
            //               <div style={{ margin: "10px 0" }}>
            //                 {new Date().toLocaleDateString("en-GB", {
            //                   weekday: "short",
            //                   day: "numeric",
            //                   month: "short",
            //                   year: "2-digit",
            //                 })}
            //               </div>
            //               <div style={{ fontSize: "10px", color: "#666" }}>
            //                 {form.getFieldValue("allergens")?.map(id => {
            //                   const allergen = allergens.find(a => a._id === id);
            //                   return allergen?.name;
            //                 }).filter(Boolean).join(", ") || "No allergens"}
            //               </div>
            //             </>
            //           )}
            //         </Form.Item>
            //       </Card>
            //       <div style={{ marginTop: 16 }}>
            //         <Tag>Height: 40 mm</Tag>
            //       </div>
            //     </div>
            //   ),
            // },
          ]}
        />

        <Divider />

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Space>
            <Button onClick={() => navigate("/label/category")}>Cancel</Button>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={isUpdating || isCreating}>
              {isEdit ? "Update Product" : "Create Product"}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
}